import React, { useMemo, useState } from 'react';
import { checkPasswordStrength, SPECIAL_CHARS_DISPLAY } from '../utils/passwordPolicy';
import PasswordInput from '../components/PasswordInput';

const EMAIL_RE = /^[^\s@]+@([^\s@]+)$/;
// A format-only check let real typos through (e.g. "gmial.com" — a
// perfectly well-formed domain shape, just not a real one). Checking
// against known domains instead catches that. Add more here as needed.
const ALLOWED_EMAIL_DOMAINS = [
  'bescom.co.in',
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'rediffmail.com',
];

const inp = {
  width: '100%',
  padding: '10px 14px',
  border: '1.5px solid rgba(211, 209, 199, 0.6)',
  borderRadius: '8px',
  fontSize: '17px',
  boxSizing: 'border-box',
  color: '#2c2c2a',
  background: 'rgba(255, 255, 255, 0.55)',
  outline: 'none',
};

const lbl = {
  display: 'block',
  fontSize: '15px',
  fontWeight: '500',
  color: '#5F5E5A',
  marginBottom: '5px',
};

const btn = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  marginTop: '8px',
};

const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };

// Generic renderer for ak-stage-prompt — works for both login's 1-field
// identifier prompt and registration's 5-field identity prompt, since it
// just loops over whatever raw.fields Authentik actually sends.
//
// Instant client-side checks (email format, password/confirm match) catch
// what used to only surface after a round trip to Authentik, which just
// re-shows this same form with a generic error (see registration.js's
// identityPromptSucceeded comment for the mismatch case this used to hit).
// Generic by field type/key, not hardcoded to registration specifically —
// harmless no-ops for login's single identifier field.
export default function PromptStep({ fields, values, setValues, loading, onSubmit, dark }) {
  const labelStyle = dark ? { ...lbl, color: 'rgba(255, 255, 255, 0.85)' } : lbl;
  const errorColor = dark ? '#FF8A80' : '#B3261E';
  const handleChange = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));
  const minOrder = Math.min(...fields.map((f) => f.order));
  const [touched, setTouched] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const fieldErrors = useMemo(() => {
    const errors = {};
    for (const field of fields) {
      const val = values[field.field_key] || '';
      if (!val) continue;
      if (field.type === 'email') {
        const match = val.match(EMAIL_RE);
        if (!match) {
          errors[field.field_key] = 'Enter a valid email address (e.g. name@gmail.com).';
        } else if (!ALLOWED_EMAIL_DOMAINS.includes(match[1].toLowerCase())) {
          errors[field.field_key] = `Use an email ending in one of: ${ALLOWED_EMAIL_DOMAINS.join(', ')}.`;
        }
      }
      if (field.field_key === 'username' && /[\s:]/.test(val)) {
        errors[field.field_key] = 'Username cannot contain spaces or colons.';
      }
      if (field.field_key === 'password') {
        const strengthError = checkPasswordStrength(val);
        if (strengthError) errors[field.field_key] = strengthError;
      }
      if (field.field_key === 'password_repeat' && values.password && val !== values.password) {
        errors[field.field_key] = 'Passwords do not match.';
      }
    }
    return errors;
  }, [fields, values]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (hasErrors) {
          setAttemptedSubmit(true);
          return;
        }
        onSubmit();
      }}
    >
      {fields.map((field) => {
        const showError = (touched[field.field_key] || attemptedSubmit) && fieldErrors[field.field_key];
        return (
          <div key={field.field_key} style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>{field.label}{field.required ? ' *' : ''}</label>
            {field.type === 'password' ? (
              <PasswordInput
                style={inp}
                placeholder={field.placeholder || ''}
                required={field.required}
                value={values[field.field_key] || ''}
                onChange={(e) => handleChange(field.field_key, e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, [field.field_key]: true }))}
                autoFocus={field.order === minOrder}
              />
            ) : (
              <input
                style={inp}
                type={field.type === 'email' ? 'email' : 'text'}
                inputMode={field.field_key === 'phone' ? 'numeric' : undefined}
                pattern={field.field_key === 'phone' ? '[0-9]{10}' : undefined}
                maxLength={field.field_key === 'phone' ? 10 : undefined}
                placeholder={field.placeholder || ''}
                required={field.required}
                value={values[field.field_key] || ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Phone field: strip anything non-numeric and cap at 10
                  // digits — this also correctly handles pasted values, which
                  // maxLength alone doesn't filter (only truncates length,
                  // not character type).
                  const val = field.field_key === 'phone'
                    ? raw.replace(/\D/g, '').slice(0, 10)
                    : raw;
                  handleChange(field.field_key, val);
                }}
                onBlur={() => setTouched((prev) => ({ ...prev, [field.field_key]: true }))}
                autoFocus={field.order === minOrder}
              />
            )}
            {showError ? (
              <p style={{ color: errorColor, fontSize: '14px', margin: '4px 0 0' }}>
                {fieldErrors[field.field_key]}
              </p>
            ) : field.field_key === 'password' && (
              <p style={{ color: dark ? 'rgba(255,255,255,0.6)' : '#8a8a86', fontSize: '12px', margin: '4px 0 0' }}>
                Must be 8+ characters with letters, numbers, and a special character ({SPECIAL_CHARS_DISPLAY})
              </p>
            )}
          </div>
        );
      })}
      <button type="submit" disabled={loading} style={loading ? btnDisabled : btn}>
        {loading ? 'Please wait…' : 'Continue'}
      </button>
    </form>
  );
}

export { inp, lbl, btn, btnDisabled };
